import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { endOfBusinessDay } from "@helpdesk/sla";

import { api, ApiError } from "../lib/api";
import { queryKeys } from "../lib/queryKeys";
import { useBusinessCalendar } from "../lib/sla";
import type { AwayKind, AwayPeriod } from "../types";

/**
 * Lunch, and off for the day.
 *
 * The SLA clock on a ticket stops while its assignee is away, and these two
 * buttons are the only way a period is ever recorded. They live in the account
 * menu because that is the one block in the product that is about the person
 * rather than the product -- an agent going to lunch has gone to lunch for the
 * CRM and the sales desk too.
 *
 * ## The rules, and why they read as they do
 *
 * Once a day each, and not reclaimable. The button therefore has three states
 * rather than two: on offer, running (with a way back), and spent. The third
 * matters -- an agent who took twenty minutes and pressed "Back" should see
 * that today's lunch is gone rather than a button that looks available and
 * answers 409, which is the version that gets reported as a bug.
 *
 * The desk being shut is handled here rather than left to the server's
 * refusal, because a button that is going to fail should not look like one
 * that is going to work. The server refuses it as well; this is the courtesy,
 * that is the authority -- the same split the WhatsApp composer uses.
 */

const LABELS: Record<AwayKind, { idle: string; running: string; spent: string }> =
  {
    lunch: {
      idle: "At lunch (1 hour)",
      running: "Back from lunch",
      spent: "Lunch taken today",
    },
    holiday: {
      idle: "Off for the rest of today",
      running: "Back — end today's leave",
      spent: "Marked off today",
    },
  };

export function AwayControls({ onDone }: { onDone?: () => void }) {
  const queryClient = useQueryClient();
  const calendar = useBusinessCalendar();

  const away = useQuery({ queryKey: queryKeys.away, queryFn: api.away });

  /*
   * Everything the clock reads, not just this list. A period starting or
   * ending changes the countdown on every ticket the agent owns, and those
   * live in the ticket queries -- which recompute from `assigneeId` and the
   * away map, so invalidating the map is enough to move them.
   */
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.away });

  const start = useMutation({
    mutationFn: (kind: AwayKind) => api.startAway(kind),
    onSuccess: () => {
      refresh();
      onDone?.();
    },
  });

  const end = useMutation({
    mutationFn: (id: number) => api.endAway(id),
    onSuccess: () => {
      refresh();
      onDone?.();
    },
  });

  const now = new Date();
  const closedToday = endOfBusinessDay(now, calendar) === null;
  const pending = start.isPending || end.isPending;

  const error = errorFrom(start.error) ?? errorFrom(end.error);

  return (
    <div>
      {(["lunch", "holiday"] as const).map((kind) => {
        const today = away.data?.mine.find((period) => period.kind === kind);
        const running = today ? isLive(today, now) : false;
        const labels = LABELS[kind];

        if (running && today) {
          return (
            <button
              key={kind}
              type="button"
              disabled={pending}
              onClick={() => end.mutate(today.id)}
              className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left max-md:py-2.5 text-sm text-content hover:bg-surface-2 disabled:opacity-60"
            >
              <span>{labels.running}</span>
              {/* What it is costing, so "back early" is an informed choice. */}
              <span className="shrink-0 text-[11px] tabular-nums text-faint">
                until {clockTime(today.endsAt, calendar.timeZone)}
              </span>
            </button>
          );
        }

        if (today) {
          return (
            <p
              key={kind}
              className="px-3 py-1.5 text-sm text-faint"
              title="One of each per day. Tomorrow it is on offer again."
            >
              {labels.spent}
            </p>
          );
        }

        return (
          <button
            key={kind}
            type="button"
            disabled={pending || closedToday || away.isLoading}
            onClick={() => start.mutate(kind)}
            className="w-full px-3 py-1.5 text-left max-md:py-2.5 text-sm text-content hover:bg-surface-2 disabled:text-faint disabled:hover:bg-transparent"
            title={
              closedToday
                ? "The desk is closed today, so there is no clock to pause."
                : "Pauses the SLA on the tickets assigned to you."
            }
          >
            {labels.idle}
          </button>
        );
      })}

      {error ? (
        <p className="px-3 pb-1 pt-0.5 text-[11px] text-sla-breached">{error}</p>
      ) : null}
    </div>
  );
}

/** Whether a period is still running. Mirrors `isLive` on the API. */
function isLive(period: AwayPeriod, now: Date): boolean {
  const end = new Date(period.endedAt ?? period.endsAt);
  return now >= new Date(period.startedAt) && now < end;
}

/**
 * "13:04" in the business calendar's zone rather than the browser's.
 *
 * The period was measured in London and an agent working from Spain reading
 * "until 14:04" would be told a time the SLA does not agree with.
 */
function clockTime(iso: string, timeZone: string): string {
  return new Date(iso).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone,
  });
}

function errorFrom(error: unknown): string | null {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return "That could not be saved.";
  return null;
}
